// Where a review comment sits on a snapshot of the pull request, and the
// link that opens the Files view there: the snapshot's own diff (base →
// snapshot, as the Snapshots tab's "Diff" opens it), at the file and lines
// the comment was anchored on, with its thread shown (`?thread=`).
import type { ReviewThread } from '../api/queries';
import { PARENT, prPath } from '../router';
import { anchorKey } from '../state/anchor';
import { carryFilter } from './filefilter';
import { COMMENT_PARAM, THREAD_PARAM, type PrRef } from './headerTargets';

/** A run of lines on one side of a diff, `start` ≤ `end`. */
export interface LineSpan {
  start: number;
  end: number;
}

/** Where a thread sits in a diff: its side and its lines there. */
export interface ThreadSpot extends LineSpan {
  side: 'LEFT' | 'RIGHT';
}

/**
 * The query parameter naming the line or lines the Files view scrolls to
 * and selects: new-side `12` or `10-12`, old-side `L12` or `L10-12`.
 */
export const LINE_PARAM = 'line';

/**
 * True when `a` and `b` name the same commit: equal, or one an abbreviation
 * (7 or more hex digits) of the other, in either case.
 */
export function sameCommit(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  const x = a.toLowerCase();
  const y = b.toLowerCase();
  if (x === y) return true;
  const [short, long] = x.length < y.length ? [x, y] : [y, x];
  return short.length >= 7 && long.startsWith(short);
}

/**
 * The lines `t` names in a diff whose new side is `commit`: GitHub's
 * current lines at the head `head` (none once the thread is outdated), its
 * original lines at the commit its first comment was written on (whether
 * or not they survive to the head). Null for a file thread, and at any
 * other commit, where neither set of line numbers applies.
 */
export function threadSpot(t: ReviewThread, commit: string | null, head: string | null): ThreadSpot | null {
  if (t.subjectType === 'FILE' || !commit) return null;
  const side = t.diffSide === 'LEFT' ? 'LEFT' : 'RIGHT';
  // A range that starts on the other side (a deleted line through an added
  // one) has a start line numbered on that side: only its end applies here.
  const oneSide = (t.startDiffSide ?? side) === side;
  const span = (line: number | null, start: number | null): ThreadSpot | null =>
    line === null ? null : { side, start: oneSide ? Math.min(start ?? line, line) : line, end: line };
  if (sameCommit(commit, head) && !t.isOutdated) {
    const at = span(t.line, t.startLine);
    if (at) return at;
  }
  if (sameCommit(commit, t.comments.nodes[0]?.originalCommit?.oid)) return span(t.originalLine, t.originalStartLine);
  return null;
}

/** Where a chip opens a review thread: a snapshot, and the thread's lines on it. */
export interface ChipTarget {
  sha: string;
  spot: ThreadSpot | null;
}

/**
 * Where the chip of a comment of `t` dated to snapshot `sha` opens: that
 * snapshot at the thread's lines there (`threadSpot`). Where the thread
 * has no lines (a reply dated to a later snapshot, a thread outdated at
 * the head), the snapshot its first comment was written on, at its
 * original lines; a file thread stays on `sha`.
 */
export function chipTarget(t: ReviewThread, sha: string, head: string | null): ChipTarget {
  const spot = threadSpot(t, sha, head);
  if (spot) return { sha, spot };
  const own = t.comments.nodes[0]?.originalCommit?.oid;
  const there = own ? threadSpot(t, own, head) : null;
  return there ? { sha: own!, spot: there } : { sha, spot: null };
}

/** What `placeThreads` needs to know of the diff shown. */
export interface ShownDiff {
  /** The commit the new side shows. */
  shown: string | null;
  /** The pull request's head. */
  head: string | null;
  /** The comparison's start (`from`): null or 'base' for the base. */
  from: string | null;
  /** True when the diff has a row for `line` on `side`. */
  has: (side: 'LEFT' | 'RIGHT', line: number) => boolean;
}

/**
 * Where `threads` go in a diff: on the row of their last line there
 * (`threadSpot`, keyed by `anchorKey`), else among the file's notes. An
 * old-side thread sits on its line only in a comparison from the base,
 * whose old side is the one it was written against.
 */
export function placeThreads(threads: ReviewThread[], d: ShownDiff): { at: Map<string, ReviewThread[]>; notes: ReviewThread[] } {
  const at = new Map<string, ReviewThread[]>();
  const notes: ReviewThread[] = [];
  const fromBase = !d.from || d.from === 'base';
  for (const t of threads) {
    const spot = threadSpot(t, d.shown, d.head);
    if (spot && (spot.side === 'RIGHT' || fromBase) && d.has(spot.side, spot.end)) {
      const key = anchorKey(spot.side, spot.end);
      at.set(key, [...(at.get(key) ?? []), t]);
    } else notes.push(t);
  }
  return { at, notes };
}

/**
 * True when the comparison shown (`mode`, null before one is) is the one
 * the URL's `from`/`to` name and none is `loading` to replace it: until
 * then a deep link's lines or thread may be found in the wrong diff.
 */
export function showsComparison(
  mode: { from: string | null; to: string | null } | null,
  query: URLSearchParams,
  loading: boolean,
): boolean {
  return !!mode && !loading && (mode.from ?? null) === query.get('from') && (mode.to ?? null) === query.get('to');
}

/** The `line` parameter's value for `s`: `12`, `10-12`, or `L10-12` on the old side. */
export function formatLines(s: LineSpan & { side?: 'LEFT' | 'RIGHT' }): string {
  return (s.side === 'LEFT' ? 'L' : '') + (s.start === s.end ? String(s.end) : `${s.start}-${s.end}`);
}

/** The lines a `line` parameter names (`12`, `10-12` in either order, `L` before for the old side), or null when it names none. */
export function parseLines(v: string | null): ThreadSpot | null {
  const m = v ? /^(L?)(\d+)(?:-(\d+))?$/.exec(v) : null;
  if (!m) return null;
  const a = Number(m[2]);
  const b = m[3] === undefined ? a : Number(m[3]);
  if (a < 1 || b < 1) return null;
  return { side: m[1] ? 'LEFT' : 'RIGHT', start: Math.min(a, b), end: Math.max(a, b) };
}

export interface SnapshotTarget {
  /** The snapshot's commit. */
  sha: string;
  /** The pull request's head: at it, the link leaves `to` out. */
  head: string;
  /** The file to open. */
  path?: string | null;
  /** The lines to scroll to and select (with `path`): new-side unless `side` says LEFT. */
  lines?: (LineSpan & { side?: 'LEFT' | 'RIGHT' }) | null;
  /** The review thread to show (by node ID). */
  thread?: string | null;
  /** The comment of that thread to scroll to (by node ID). */
  comment?: string | null;
}

/**
 * The Files view at snapshot `t.sha`, compared from base (`to=<sha>`, no
 * `from`), at `t.path` and `t.lines`, showing `t.thread` (`?thread=`,
 * `?comment=`). The current query lends only its file filter: the
 * reader's own range is replaced by the snapshot's.
 */
export function snapshotHref(pr: PrRef, t: SnapshotTarget, query: URLSearchParams): string {
  const q = new URLSearchParams();
  if (t.path) q.set('file', t.path);
  if (t.path && t.lines) q.set(LINE_PARAM, formatLines(t.lines));
  if (!sameCommit(t.sha, t.head)) q.set('to', t.sha);
  if (t.thread) q.set(THREAD_PARAM, t.thread);
  if (t.thread && t.comment) q.set(COMMENT_PARAM, t.comment);
  const s = q.toString();
  return carryFilter(prPath(pr.owner, pr.repo, pr.number, 'files') + (s ? '?' + s : ''), query);
}

/**
 * The Files view (path and query, without the reader's file filter) at the
 * changes commits `first` through `last` make together: from `first`'s
 * parent to `last` (`from=<first>^&to=<last>`). One commit (`last`
 * omitted) is that commit's own changes, as GitHub's
 * `/pull/N/commits/<sha>` opens it here.
 */
export function commitPath(pr: PrRef, first: string, last: string = first): string {
  const q = new URLSearchParams({ from: first + PARENT, to: last });
  return `${prPath(pr.owner, pr.repo, pr.number, 'files')}?${q}`;
}

/** `commitPath` for one commit, keeping the reader's file filter (their own range is replaced). */
export function commitHref(pr: PrRef, sha: string, query: URLSearchParams): string {
  return carryFilter(commitPath(pr, sha), query);
}

/** `commitPath` for a run of commits, keeping the reader's file filter (their own range is replaced). */
export function commitsHref(pr: PrRef, first: string, last: string, query: URLSearchParams): string {
  return carryFilter(commitPath(pr, first, last), query);
}
